import {
  CardHeader,
  CardContent,
  CardTitle,
  CardFooter,
} from '@/components/ui/card'
import { AlertTriangle, AlertCircle } from 'lucide-react'
import { type NotificationItemProps } from './types'
import { formatTransactionDate } from './utils'
import { MarkAsReadButton } from '.'
import { DefaultNotification } from './DefaultNotification'

interface ServerFailureMetadata {
  server_id?: number
  server_name?: string
  exit_code?: number
  terminal_reason?: string
  terminal_message?: string
  failed_at?: string
}

export const ServerFailureNotification = ({
  notification,
  isLoading,
  onMarkAsRead,
}: NotificationItemProps) => {
  if (!notification.metadata) {
    return (
      <DefaultNotification
        notification={notification}
        isLoading={isLoading}
        onMarkAsRead={onMarkAsRead}
      />
    )
  }

  const metadata = notification.metadata as ServerFailureMetadata
  const serverName = metadata.server_name || 'Unknown Server'

  return (
    <>
      <CardHeader className="pt-5 pb-3 px-6">
        <div className="flex items-start gap-4">
          <div className="flex items-center justify-center h-10 w-10 rounded-full flex-shrink-0 bg-amber-500/10">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg font-bold flex items-center gap-2 flex-wrap">
              Server Failure Alert
            </CardTitle>
            <p className="text-sm text-foreground/60 mt-1">
              <span className="font-medium">{serverName}</span> has stopped
              running
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-6 py-4">
        <div className="space-y-5">
          {/* Failure Summary */}
          <div className="p-4 rounded-lg bg-background/10 border border-background/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <div className="space-y-2 flex-1">
                <p className="text-sm font-medium">
                  {metadata.terminal_reason ||
                    'Application completed execution'}
                  {metadata.terminal_message &&
                    `: ${metadata.terminal_message}`}
                </p>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="text-sm">
            {/* Technical details section removed since server might be garbage collected */}
          </div>

          <p className="text-xs text-foreground/60 mt-4">
            Updated on {formatTransactionDate(notification.created_at)}
          </p>
        </div>
      </CardContent>

      {!notification.is_read && (
        <CardFooter className="px-6 py-3 flex justify-end border-t">
          <MarkAsReadButton
            isLoading={isLoading}
            onMarkAsRead={() => onMarkAsRead(notification.notification_id)}
          />
        </CardFooter>
      )}
    </>
  )
}
